import { AfterViewInit, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { CREATE_LABEL, PRIMENG_CALENDAR_DATE, SAVE_CHANGES_LABEL } from '@microsec/constants';
import { BaseComponent } from '@ids-components';
import {
  AsteriskComponent,
  CardComponent,
  CommonTableComponent,
  CustomMarkdownEditorComponent,
  CustomStepsComponent,
  FormBuilderComponent,
  TruncateTextComponent,
} from '@microsec/components';
import { FormItem } from '@microsec/models';
import { AssessmentService } from '@ids-services';
import { UserService } from '@microsec/services';

import moment from 'moment';
import { Observable, catchError, finalize, forkJoin, of } from 'rxjs';
import { COMPLIANCE_STANDARD_IEC_62443_KEY } from '@ids-constants';
import { SharedDeviceTableComponent } from '../../../../../shared-components/shared-device-table/shared-device-table.component';
import { MenuItem } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ArrayMapPipe, ConstantPipe } from '@ids-pipes';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';

export const FORM_PARAMS = {
  ORGANIZATION_ID: 'organization_id',
  PROJECT_ID: 'project_id',
  NAME: 'name',
  DESCRIPTION: 'description',
  ASSESSMENT_TYPE_ID: 'assessment_type_id',
  // ASSIGNEE_USER_ID: 'assignee_user_id',
  DEVICES: 'devices',
  SCHEDULED: 'scheduled',
  USE_PREFILLING: 'use_prefilling',
  MIN_CONFIDENCE_SCORE: 'min_confidence_score',
  // Helper Params
  IS_DEVICE_SPECIFIC: 'isDeviceSpecific',
  CONFIGURATION_TEMPLATE: 'configurationTemplate',
  DEVICES_TEMPLATE: 'devicesTemplate',
  PREFILLING_TEMPLATE: 'prefillingTemplate',
  GENERAL_TEMPLATE: 'generalTemplate',
};

const PREFILLING_SCORE_LEVEL: { [key: number]: string } = {
  1: '0.8',
  2: '0.5',
  3: '0.0',
};

@Component({
  selector: 'app-assessment-form',
  templateUrl: './assessment-form.component.html',
  styleUrls: ['./assessment-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SliderModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    DividerModule,
    TooltipModule,
    SelectModule,
    InputTextModule,
    ToggleSwitchModule,
    FormBuilderComponent,
    CustomStepsComponent,
    CommonTableComponent,
    CardComponent,
    AsteriskComponent,
    TruncateTextComponent,
    CustomMarkdownEditorComponent,
    ArrayMapPipe,
    ConstantPipe,
    SharedDeviceTableComponent,
  ],
  providers: [ArrayMapPipe, ConstantPipe],
})
export class AssessmentFormComponent extends BaseComponent implements AfterViewInit {
  _assessment: any = null;

  get assessment() {
    return this._assessment;
  }

  @Input() set assessment(value: any) {
    this._assessment = value;
  }

  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();

  activeStep = 0;

  CREATE_LABEL = CREATE_LABEL;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  FORM_PARAMS = FORM_PARAMS;

  PRIMENG_CALENDAR_DATE = PRIMENG_CALENDAR_DATE;

  PREFILLING_SCORE_LEVEL = PREFILLING_SCORE_LEVEL;

  fields: FormItem[] = [];

  selectedDeviceIds: any[] = [];

  selectedDevices: any[] = [];

  cachedSelectedDevices: any[] = [];

  checkNonCompliance = false;

  assessmentTypes: any[] = [];

  assessmentTypeOptions: any[] = [];

  // assigneeOptions: any[] = [];

  prefillingInfo: any = null;

  steps: MenuItem[] = [];

  selectedPrefillingLevel = 2;

  scheduledMinDate = moment().toDate();

  showBreakdown = false;

  cols: any[] = [
    { field: 'label', header: 'Name', width: 15 },
    { field: 'type', header: 'Type', width: 10 },
    { field: 'zones', header: 'Zones', width: 10 },
    { field: 'prefilling', header: 'Prefillable Questions', width: 10 },
  ];

  serverErrors: any = null;

  @ViewChild('fb') form!: FormBuilderComponent;

  @ViewChild('stepsTemplate') stepsTemplate!: TemplateRef<any>;

  @ViewChild('titleTemplate') titleTemplate!: TemplateRef<any>;

  @ViewChild('configurationTemplate') configurationTemplate!: TemplateRef<any>;

  @ViewChild('devicesTemplate') devicesTemplate!: TemplateRef<any>;

  @ViewChild('prefillingTemplate') prefillingTemplate!: TemplateRef<any>;

  @ViewChild('generalTemplate') generalTemplate!: TemplateRef<any>;

  @ViewChild('deviceTable') deviceTable!: SharedDeviceTableComponent;

  constructor(
    private assessmentSrv: AssessmentService,
    private userSrv: UserService,
  ) {
    super();
  }

  async ngAfterViewInit() {
    await this.prepareConfigs();
    this.activeStep = 0;
    this.getData(() => {
      this.initForm();
    });
  }

  initForm() {
    this.setSteps(!!this.assessment ? !!this.assessment?.[FORM_PARAMS.DEVICES]?.length : false);
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NAME,
        required: true,
        defaultValue: this.assessment?.[FORM_PARAMS.NAME] || '',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.DESCRIPTION,
        defaultValue: this.assessment?.[FORM_PARAMS.DESCRIPTION] || '',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.ASSESSMENT_TYPE_ID,
        required: true,
        defaultValue: this.assessment?.assessment_type?.id || '',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.SCHEDULED,
        defaultValue: this.assessment?.[FORM_PARAMS.SCHEDULED]
          ? moment
              .utc(this.assessment?.[FORM_PARAMS.SCHEDULED])
              .local()
              .toDate()
          : null,
        hidden: true,
      } as FormItem),
      // Object.assign(new FormItem(), {
      //   name: FORM_PARAMS.ASSIGNEE_USER_ID,
      //   defaultValue: this.assessment?.[FORM_PARAMS.ASSIGNEE_USER_ID] || null,
      //   hidden: true,
      // } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.IS_DEVICE_SPECIFIC,
        defaultValue: !!this.assessment ? !!this.assessment?.[FORM_PARAMS.DEVICES]?.length : false,
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.DEVICES,
        defaultValue: this.selectedDeviceIds,
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.USE_PREFILLING,
        defaultValue: this.assessment?.[FORM_PARAMS.USE_PREFILLING] || true,
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.MIN_CONFIDENCE_SCORE,
        defaultValue: this.assessment?.[FORM_PARAMS.MIN_CONFIDENCE_SCORE] || 0,
        hidden: true,
      } as FormItem),

      Object.assign(new FormItem(), {
        hasNoLabel: true,
        field: 'custom',
        customField: this.stepsTemplate,
      } as FormItem),
      Object.assign(new FormItem(), {
        hasNoLabel: true,
        field: 'custom',
        customField: this.titleTemplate,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.CONFIGURATION_TEMPLATE,
        hasNoLabel: true,
        field: 'custom',
        customField: this.configurationTemplate,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.DEVICES_TEMPLATE,
        hasNoLabel: true,
        field: 'custom',
        customField: this.devicesTemplate,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.PREFILLING_TEMPLATE,
        hasNoLabel: true,
        field: 'custom',
        customField: this.prefillingTemplate,
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.GENERAL_TEMPLATE,
        hasNoLabel: true,
        field: 'custom',
        customField: this.generalTemplate,
        hidden: true,
      } as FormItem),
    ];
    this.fields = fields;
    setTimeout(() => {
      this.form?.setChangeEvent(FORM_PARAMS.IS_DEVICE_SPECIFIC, (value: boolean) => {
        this.setSteps(value);
      });
      this.form?.setChangeEvent(FORM_PARAMS.ASSESSMENT_TYPE_ID, (value: any) => {
        this.setCheckNonCompliance(value, true);
        if (!this.assessment) {
          this.form.setControlValue(
            FORM_PARAMS.NAME,
            `${this.assessmentTypeOptions.find((type) => type.value === value)?.label} (${moment().format('YYYYMMDD_HHmmss')})`,
          );
        }
      });
    });
  }

  setCheckNonCompliance(assessmentTypeId: any, resetDevice = false) {
    const assessmentType = (this.assessmentTypes || []).find((assessmentType) => assessmentType.id === assessmentTypeId);
    this.checkNonCompliance = assessmentType?.standard === COMPLIANCE_STANDARD_IEC_62443_KEY;
    if (!!resetDevice && !!this.checkNonCompliance && this.deviceTable) {
      this.deviceTable.selectedDevices = [];
    }
  }

  getData(callback: () => void) {
    this.form.isLoading = true;
    forkJoin({
      ...(!!this.assessment ? { assessment: this.assessmentSrv.getAssessment(this.assessment?.id) } : {}),
      assessmentTypesData: this.assessmentSrv
        .getAssessmentTypes(this.breadcrumbConfig?.organizationId, this.breadcrumbConfig?.projectId, undefined, undefined, 'id', true)
        .pipe(catchError(() => of(null))),
    })
      .pipe(
        finalize(() => {
          this.form.isLoading = false;
          if (!!this.assessment?.assessment_type?.id) {
            this.setCheckNonCompliance(this.assessment?.assessment_type?.id);
          }
          callback();
        }),
      )
      .subscribe({
        next: (res) => {
          if (!!!!this.assessment) {
            this._assessment = res?.assessment;
            this.selectedDeviceIds = ((this.assessment?.[FORM_PARAMS.DEVICES] as any[]) || []).map((d) => d.device_id);
          }
          this.assessmentTypes = res?.assessmentTypesData?.assessment_types || [];
          this.assessmentTypeOptions = this.assessmentTypes.map((assessmentType) => ({
            label: assessmentType.name,
            value: assessmentType.id,
          }));
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  getAssessmentPrefillingInfo() {
    const assessmentTypeId = this.form?.getControlValue(FORM_PARAMS.ASSESSMENT_TYPE_ID);
    const isDeviceSpecific = this.form?.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC);
    if (!!assessmentTypeId && !!isDeviceSpecific && !!this.selectedDeviceIds?.length) {
      this.form.isLoading = true;
      this.assessmentSrv
        .getAssessmentPrefillingInfo(assessmentTypeId, this.selectedDeviceIds)
        .pipe(
          finalize(() => {
            this.form.isLoading = false;
          }),
        )
        .subscribe({
          next: (res) => {
            this.prefillingInfo = res || null;
            const selectedDevices = (this.selectedDevices || []).map((device) => ({
              ...device,
              prefilling: {
                1: {
                  total_questions: this.prefillingInfo?.total_questions || 0,
                  total_prefillable_questions:
                    (this.prefillingInfo?.confidence_scores?.[PREFILLING_SCORE_LEVEL[1]]?.per_device || []).find(
                      (d: any) => d.device_id === device.id,
                    )?.device_prefillable_questions || 0,
                },
                2: {
                  total_questions: this.prefillingInfo?.total_questions || 0,
                  total_prefillable_questions:
                    (this.prefillingInfo?.confidence_scores?.[PREFILLING_SCORE_LEVEL[2]]?.per_device || []).find(
                      (d: any) => d.device_id === device.id,
                    )?.device_prefillable_questions || 0,
                },
                3: {
                  total_questions: this.prefillingInfo?.total_questions || 0,
                  total_prefillable_questions:
                    (this.prefillingInfo?.confidence_scores?.[PREFILLING_SCORE_LEVEL[3]]?.per_device || []).find(
                      (d: any) => d.device_id === device.id,
                    )?.device_prefillable_questions || 0,
                },
              },
            }));
            this.selectedDevices = selectedDevices;
          },
          error: (error) => {
            this.showErrorMessage(error);
          },
        });
    }
  }

  onSubmit() {
    this.form.isLoading = true;
    const formValue = this.form.getRawValue();
    const payload = {
      [FORM_PARAMS.NAME]: formValue[FORM_PARAMS.NAME],
      [FORM_PARAMS.DESCRIPTION]: formValue[FORM_PARAMS.DESCRIPTION],
      [FORM_PARAMS.ASSESSMENT_TYPE_ID]: formValue[FORM_PARAMS.ASSESSMENT_TYPE_ID],
      // [FORM_PARAMS.ASSIGNEE_USER_ID]: formValue[FORM_PARAMS.ASSIGNEE_USER_ID],
      [FORM_PARAMS.USE_PREFILLING]: formValue[FORM_PARAMS.USE_PREFILLING],
      [FORM_PARAMS.MIN_CONFIDENCE_SCORE]: parseFloat(PREFILLING_SCORE_LEVEL[this.selectedPrefillingLevel]),
      ...(!this.assessment && {
        [FORM_PARAMS.ORGANIZATION_ID]: this.breadcrumbConfig?.organizationId,
        [FORM_PARAMS.PROJECT_ID]: this.breadcrumbConfig?.projectId,
      }),
      [FORM_PARAMS.SCHEDULED]: !!formValue[FORM_PARAMS.SCHEDULED] ? moment(formValue[FORM_PARAMS.SCHEDULED]).toISOString() : undefined,
      [FORM_PARAMS.DEVICES]: !!formValue[FORM_PARAMS.IS_DEVICE_SPECIFIC] && this.selectedDeviceIds?.length ? this.selectedDeviceIds : [],
    };
    const request: Observable<any> = !this.assessment
      ? this.assessmentSrv.createAssessment(payload)
      : this.assessmentSrv.updateAssessment(this.assessment.id, payload);
    request
      .pipe(
        finalize(() => {
          this.form.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          this.showSuccessMessage(`Assessment ${!!this.assessment ? 'Updated' : 'Created'} Successfully`);
          this.closeEvent?.emit(rs);
        },
        error: (err: any) => {
          this.form.showServerErrorMessage(err);
          this.showErrorMessage(err);
        },
      });
  }

  setSteps(isDeviceSpecific: boolean) {
    this.steps = [
      {
        label: 'Assessment Type & Scope',
      },
      ...(!isDeviceSpecific
        ? []
        : [
            {
              label: 'Assessment Prefilling',
            },
          ]),
      {
        label: 'Finalize Assessment Information',
      },
    ];
  }

  onStepChange(step: number) {
    this.activeStep += step;
    if (this.activeStep === 1 && !!this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC) && step > 0) {
      this.getAssessmentPrefillingInfo();
    }
    this.form.setControlVisibility(FORM_PARAMS.CONFIGURATION_TEMPLATE, this.activeStep === 0);
    this.form.setControlVisibility(FORM_PARAMS.DEVICES_TEMPLATE, this.activeStep === 0);
    this.form.setControlVisibility(
      FORM_PARAMS.PREFILLING_TEMPLATE,
      this.activeStep === 1 && !!this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC),
    );
    this.form.setControlVisibility(
      FORM_PARAMS.GENERAL_TEMPLATE,
      (this.activeStep === 1 && !this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC)) || this.activeStep === 2,
    );
    setTimeout(() => {
      if (!!this.serverErrors) {
        this.form.showServerErrorMessage(this.serverErrors);
      }
    }, 100);
  }

  getPrefillingPercentage(level: number) {
    return parseFloat(PREFILLING_SCORE_LEVEL[level]) * 100;
  }

  get isCurrentStepValid() {
    switch (this.activeStep) {
      case 0: {
        return (
          this.form?.getControl(FORM_PARAMS.ASSESSMENT_TYPE_ID)?.valid &&
          this.form?.getControl(FORM_PARAMS.IS_DEVICE_SPECIFIC)?.valid &&
          this.form?.getControl(FORM_PARAMS.DEVICES)?.valid &&
          ((!!this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC) && !!this.selectedDeviceIds?.length) ||
            !this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC))
        );
      }
      case 1: {
        return !!this.form.getControlValue(FORM_PARAMS.IS_DEVICE_SPECIFIC);
      }
      default: {
        return false;
      }
    }
  }
}
